import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import useAutoSelectSingle from '../hooks/useAutoSelectSingle';

export default function AdminTools() {
  const [importMsg, setImportMsg] = useState('');
  const [exportMsg, setExportMsg] = useState('');
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  // --- Bulk import (CSV) state ---
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [bulkDate, setBulkDate] = useState(new Date().toISOString().slice(0, 10));
  const [parsedRows, setParsedRows] = useState([]);
  const [bulkMsg, setBulkMsg] = useState('');
  const csvFileRef = useRef(null);
  const [csvFileName, setCsvFileName] = useState('');

  useEffect(() => {
    api.get('/stores').then(r => setStores(r.data));
  }, []);

  useAutoSelectSingle(stores, storeId, setStoreId);

  const handleExport = async () => {
    setExportMsg('');
    const token = localStorage.getItem('token');
    const url = `${api.defaults.baseURL}/admin/export`;
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      // Never download the body of a failed response — that's how an expired
      // session ends up saved as a bogus "export" file.
      if (!r.ok) {
        if (r.status === 401) {
          localStorage.clear();
          window.location.href = '/login';
          return;
        }
        let detail = `Error ${r.status}`;
        try { detail = (await r.json()).detail || detail; } catch (e) { /* non-JSON */ }
        setExportMsg(`No se pudo exportar: ${detail}`);
        return;
      }
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.setAttribute('download', `inventory_export_${new Date().toISOString().slice(0, 10)}.json`);
      a.click();
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      setExportMsg(`No se pudo exportar: ${err.message}`);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm('¡Esto ELIMINARÁ todos los datos locales y los reemplazará con el archivo importado. ¿Continuar?')) {
      fileRef.current.value = '';
      return;
    }
    setImporting(true);
    setImportMsg('');
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const res = await api.post('/admin/import', payload);
      const counts = Object.entries(res.data.counts)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
      setImportMsg(`Importación completa — ${counts}`);
    } catch (err) {
      setImportMsg(err.response?.data?.detail || err.message || 'Error en la importación');
    } finally {
      setImporting(false);
      fileRef.current.value = '';
    }
  };

  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFileName(file.name);
    setParsedRows([]);
    setBulkMsg('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) {
        setBulkMsg('El CSV debe tener una fila de encabezado y al menos una fila de datos');
        return;
      }

      const header = lines[0].split(',').map(h => h.trim().toLowerCase());
      const itemIdx = header.indexOf('item');
      const categoryIdx = header.indexOf('category');
      const countIdx = header.indexOf('count');

      if (itemIdx === -1 || countIdx === -1) {
        setBulkMsg('El CSV debe tener las columnas "item" y "count". La columna "category" es opcional.');
        return;
      }

      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim());
        const item = cols[itemIdx] || '';
        const category = categoryIdx !== -1 ? (cols[categoryIdx] || '') : '';
        const count = cols[countIdx] || '';

        if (!item || !count) continue;

        const parsed = parseInt(count, 10);
        if (isNaN(parsed)) continue;

        rows.push({ item, category, count: parsed });
      }

      if (rows.length === 0) {
        setBulkMsg('No se encontraron filas válidas en el CSV');
        return;
      }

      setParsedRows(rows);
      setBulkMsg(`Se analizaron ${rows.length} filas del CSV`);
    };
    reader.readAsText(file);
  };

  const submitBulk = async (e) => {
    e.preventDefault();
    setBulkMsg('');

    if (parsedRows.length === 0) {
      setBulkMsg('Sube un archivo CSV primero');
      return;
    }

    try {
      const res = await api.post('/transactions/bulk-import-csv', {
        store_id: parseInt(storeId),
        items: parsedRows,
        date: bulkDate,
      });
      setBulkMsg(`¡Importación completa! ${res.data.created} artículos nuevos creados, ${res.data.updated} artículos existentes actualizados.`);
      setParsedRows([]);
      setCsvFileName('');
      if (csvFileRef.current) csvFileRef.current.value = '';
    } catch (err) {
      setBulkMsg(err.response?.data?.detail || 'Error');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Herramientas de Admin</h1>
      </div>

      <div className="card" style={{ maxWidth: 480 }}>
        <h3 style={{ marginBottom: 8 }}>Exportar Datos</h3>
        <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 16 }}>
          Descarga un snapshot completo en JSON de todas las tiendas, artículos, transacciones,
          conciliaciones y usuarios.
        </p>
        {exportMsg && (
          <div className="alert alert-danger" style={{ marginBottom: 12 }}>{exportMsg}</div>
        )}
        <button className="btn btn-primary" onClick={handleExport}>
          Descargar Exportación
        </button>
      </div>

      <div className="card" style={{ maxWidth: 480 }}>
        <h3 style={{ marginBottom: 8 }}>Importar Datos</h3>
        <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 16 }}>
          Carga un archivo JSON exportado previamente. <strong>Todos los datos existentes serán eliminados.</strong>
        </p>
        {importMsg && (
          <div className={`alert ${importMsg.startsWith('Importación completa') ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: 12 }}>
            {importMsg}
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".json"
          onChange={handleImport}
          disabled={importing}
          style={{ width: 'auto' }}
        />
        {importing && <p style={{ marginTop: 8, fontSize: 14, color: 'var(--text-2)' }}>Importando...</p>}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 8 }}>Importación Masiva</h3>
        <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 16 }}>
          Sube un archivo CSV con las columnas <strong>item</strong>, <strong>category</strong> y <strong>count</strong>.
          Los artículos nuevos se crearán automáticamente. Los artículos existentes tendrán su categoría y conteo actualizados.
        </p>

        {bulkMsg && (
          <div className={`alert ${bulkMsg.includes('Error') ? 'alert-danger' : bulkMsg.includes('completa') ? 'alert-success' : 'alert-info'}`} style={{ marginBottom: 12 }}>
            {bulkMsg}
          </div>
        )}

        <form onSubmit={submitBulk}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <div className="form-group" style={{ minWidth: 200 }}>
              <label>Tienda</label>
              <select value={storeId} onChange={e => setStoreId(e.target.value)} required>
                <option value="">Seleccionar tienda</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ minWidth: 160 }}>
              <label>Fecha</label>
              <input type="date" value={bulkDate} onChange={e => setBulkDate(e.target.value)} required />
            </div>
            <div className="form-group" style={{ minWidth: 250 }}>
              <label>Archivo CSV</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => csvFileRef.current?.click()}
                >
                  Seleccionar archivo
                </button>
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  {csvFileName || 'Ningún archivo seleccionado'}
                </span>
                <input type="file" accept=".csv" onChange={handleCsvFile} ref={csvFileRef} style={{ display: 'none' }} />
              </div>
            </div>
          </div>

          {parsedRows.length > 0 && (
            <>
              <h4 style={{ marginBottom: 12 }}>Vista previa ({parsedRows.length} artículos)</h4>
              <div className="table-wrapper" style={{ maxHeight: 400, overflow: 'auto', marginBottom: 16 }}>
                <table>
                  <thead><tr><th>Artículo</th><th>Categoría</th><th>Conteo</th></tr></thead>
                  <tbody>
                    {parsedRows.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.item}</td>
                        <td>{row.category || '-'}</td>
                        <td>{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div style={{ marginTop: 16 }}>
            <button type="submit" className="btn btn-primary" disabled={parsedRows.length === 0}>
              Importar {parsedRows.length > 0 ? `${parsedRows.length} Artículos` : ''}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
